// ═══════════════════════════════════════════════════════════
// Public Requester Account API
//
// Public users register/log in through the public portal.
// They are separate from GAB staff users — stored in the
// publicUsers table. Authentication is a simple email +
// hashed password stored in GAB (no OAuth for now).
//
// !! Passwords are hashed client-side with SHA-256 before
// !! storage. This is not cryptographically ideal but works
// !! without a dedicated auth service. In production, use
// !! a proper auth service or Cloudflare Workers KV.
// ═══════════════════════════════════════════════════════════

import { queryRecords, createRecord, updateRecord } from './client';
import { TABLES } from './tables';
import { FIELDS } from './fields';

const F = FIELDS.publicUsers;

export interface PublicUser {
  recordId:   number;
  email:      string;
  firstName:  string;
  lastName:   string;
  isVerified: boolean;
  createdAt:  string;
  lastLoginAt?:string;
}

export interface PublicAuthState {
  user: PublicUser | null;
  token: string | null; // simple session token (recordId + email encoded)
}

const SESSION_KEY = 'foia_public_session';
const store = localStorage; // persist across tabs and browser restarts

async function sha256(str: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

function rowToUser(row: Record<string, unknown>): PublicUser {
  return {
    recordId:    row['record_id'] as number,
    email:       String(row[F.email] ?? ''),
    firstName:   String(row[F.firstName] ?? ''),
    lastName:    String(row[F.lastName] ?? ''),
    isVerified:  row[F.isVerified] === true || row[F.isVerified] === 'true',
    createdAt:   String(row[F.createdAt] ?? ''),
    lastLoginAt: row[F.lastLoginAt] as string | undefined,
  };
}

function makeSessionToken(user: PublicUser): string {
  return btoa(JSON.stringify({ id: user.recordId, email: user.email, ts: Date.now() }));
}

export function getPublicSession(): PublicAuthState {
  try {
    const raw = store.getItem(SESSION_KEY);
    if (!raw) return { user: null, token: null };
    return JSON.parse(raw) as PublicAuthState;
  } catch {
    return { user: null, token: null };
  }
}

function savePublicSession(user: PublicUser): string {
  const token = makeSessionToken(user);
  store.setItem(SESSION_KEY, JSON.stringify({ user, token }));
  return token;
}

export function clearPublicSession() {
  store.removeItem(SESSION_KEY);
}

export async function registerPublicUser(input: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}): Promise<{ user: PublicUser; token: string }> {
  // Check if email already exists (use limit: 200 — GAB ignores filters with limit: 1)
  const { records: existing } = await queryRecords(TABLES.publicUsers, {
    filters: [{ field: F.email, operator: 'eq', value: input.email.toLowerCase().trim() }],
    limit: 200,
  }, { anonymous: true });

  if ((existing as unknown[]).length > 0) {
    throw new Error('An account with this email already exists.');
  }

  const hash = await sha256(input.password + input.email.toLowerCase());
  const now = new Date().toISOString();

  const row = await createRecord(TABLES.publicUsers, {
    [F.email]:     input.email.toLowerCase().trim(),
    [F.firstName]: input.firstName.trim(),
    [F.lastName]:  input.lastName.trim(),
    [F.passwordHash]: hash,
    [F.isVerified]:   false,
    [F.createdAt]:    now,
    [F.lastLoginAt]:  now,
  }, { anonymous: true });

  const user = rowToUser(row as Record<string, unknown>);
  const token = savePublicSession(user);
  return { user, token };
}

export async function loginPublicUser(input: {
  email: string;
  password: string;
}): Promise<{ user: PublicUser; token: string }> {
  const { records } = await queryRecords(TABLES.publicUsers, {
    filters: [{ field: F.email, operator: 'eq', value: input.email.toLowerCase().trim() }],
    limit: 200,
  }, { anonymous: true });

  const row = (records as Record<string, unknown>[])[0];
  if (!row) throw new Error('No account found with this email.');

  const hash = await sha256(input.password + input.email.toLowerCase());
  if (row[F.passwordHash] !== hash) throw new Error('Incorrect password.');

  // Update last login
  await updateRecord(TABLES.publicUsers, row['record_id'] as number, {
    [F.lastLoginAt]: new Date().toISOString(),
  }, { anonymous: true } as Parameters<typeof updateRecord>[3]);

  const user = rowToUser(row);
  const token = savePublicSession(user);
  return { user, token };
}

// Get requests submitted by this public user (matched by email on the requester record)
export async function getMyRequests(requesterEmail: string) {
  // Find all requester records with this email
  // Use limit: 2000 — GAB ignores filters server-side, applies client-side after fetch
  const { records: requesterRows } = await queryRecords(
    TABLES.requesters,
    { filters: [{ field: FIELDS.requesters.email, operator: 'eq', value: requesterEmail.toLowerCase().trim() }], limit: 2000 },
    { anonymous: true },
  );
  const requesterIds = new Set(
    (requesterRows as Record<string, unknown>[]).map(r => Number(r['record_id']))
  );
  if (!requesterIds.size) return [];

  // Fetch all requests then filter by requester IDs client-side
  const { records: allRows } = await queryRecords(
    TABLES.requests,
    {
      sort: { field: FIELDS.requests.dateSubmitted, direction: 'desc' },
      limit: 2000,
    },
    { anonymous: true },
  );
  const allRequests = (allRows as Record<string, unknown>[]).filter(r =>
    requesterIds.has(Number(r[FIELDS.requests.requesterId]))
  );

  // Sort combined results by dateSubmitted descending
  return allRequests.sort((a, b) => {
    const da = String(a[FIELDS.requests.dateSubmitted] ?? '');
    const db = String(b[FIELDS.requests.dateSubmitted] ?? '');
    return db.localeCompare(da);
  });
}
